import React, { createContext, useState, useEffect } from 'react';
import uuid from 'uuid';
import _ from 'lodash';
import moment from 'moment';

import { useAuth } from 'contexts/auth';
import { useWebsocket } from 'contexts/websocket';
import courseRequests from 'services/course';
import groupRequest from 'services/group';
import ltiService from 'services/lti';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { DownloadFileFromURL, getUserPreferredFullName, stripHTML } from 'utils/helper';
import { useLocation } from 'react-router-dom';

const PageContext = createContext();

const DIALOG = {
    PEER_EVALUATION_PUBLISH: 'peer-evaluation-publish',
    PEER_EVALUATION_UNPUBLISH: 'peer-evaluation-unpublish',
    PEER_EVALUATION_DELETE: 'peer-evaluation-delete',
    PEER_EVALUATION_SAVE: 'peer-evaluation-save',
    PEER_EVALUATION_CANCEL: 'peer-evaluation-cancel',
    PEER_EVALUATION_RELEASE_GRADES: 'peer-evaluation-release-grades',
    PEER_EVALUATION_NOTIFICATIONS_TO_ALL: 'peer-evaluation-notification-to-all',
    PEER_EVALUATION_NOTIFICATIONS_TO_STUDENT: 'peer-evaluation-notification-to-student',
};

const initialPeerEvaluationState = {
    active: true,
    title: '',
    format: null,
    visibility: { all_teams: true, visible_to: [] },
    start_on: 0,
    end_on: 0,
    due_on: 0,
    publish_on: 0,
    publish_time: 0,
    status: 'draft',
    release_to_students: false,
    release_on: null,
    release_time: null,
    resources: [],
    keep_result_anonymous: false,
    min_feedback_length: '',
    details: '',
    mode: 'create',
};

const initialRubricData = [
    {
        id: uuid.v4(),
        title: 'Criterion Name',
        description: 'Description',
        options: [
            {
                id: uuid.v4(),
                title: '',
                description: '',
                rate: '20',
            },
            {
                id: uuid.v4(),
                title: '',
                description: '',
                rate: '0',
            },
        ],
    },
];

const STUDENT_STEPS = {
    START: 'start',
    ZERO_SUM: 'zero-sum',
    RUBRIC: 'rubric',
    NO_RESULT: 'no-result',
    RESULT: 'result',
};

const INSTRUCTOR_PAGES = {
    TABLE: 'table',
    SUBMISSIONS: 'submissions',
    CREATE: 'create',
    EDIT: 'edit',
};

let ZEROSUM_CSV_HEADER = [];

let RUBRIC_CSV_HEADER = [];

const PageProvider = (props) => {
    const location = useLocation();
    const { auth, isLTI } = useAuth();
    const { message } = useSnackbar();
    const { course, groups, studentAccountUsers } = useUser2();
    const { sendOn } = useWebsocket();

    const [dialog, setDialog] = useState({
        open: false,
        message: '',
        description: '',
        variant: 'primary',
        actionButtonText: '',
        onAction: () => {},
    });

    const [buttonLoading, setButtonLoading] = useState(false);
    const [instructorPage, setInstructorPage] = useState(INSTRUCTOR_PAGES.TABLE);
    const [peerEvaluationList, setPeerEvaluationList] = useState([]);
    const [currentPeerEvaluationId, setCurrentPeerEvaluationId] = useState();
    const [currentPeerEvaluation, setCurrentPeerEvaluation] = useState();
    const [isViewingSubmission, setIsViewingSubmission] = useState(false);
    const [submissions, setSubmissions] = useState([]);
    const [studentStep, setStudentStep] = useState(STUDENT_STEPS.START);

    // Create Peer Evaluation Form
    const [form, setForm] = useState(initialPeerEvaluationState);

    // LTI
    const [pushGradeLoading, setPushGradeLoading] = useState(false);
    const [LTIAssignments, setLTIAssignments] = useState([]);

    // Student Submission Data
    const [submissionLoading, setSubmissionLoading] = useState(false);
    const [selectedPeerEvaluation, setSelectedPeerEvaluation] = useState({});
    const [submissionData, setSubmissionData] = useState({});
    const [rubricData, setRubricData] = useState(initialRubricData);
    const [isEditing, setIsEditing] = useState(false);

    const getFormat = (data) => {
        if (data.format === 'rubric') {
            return { title: 'Rubric', value: 'rubric' };
        } else if (data.format === 'zero-sum') {
            return { title: 'Zero-Sum Game', value: 'zero-sum' };
        }
    };

    const createPeerEvaluation = async (data) => {
        try {
            setButtonLoading(true);
            handleInstructorPages(INSTRUCTOR_PAGES.TABLE);
            const { resources = [] } = data;
            let listOfResources = [];
            const peerEvaluationId = data.id || uuid.v4();
            if (resources.length > 0) {
                const uploadFiles = resources.map(async (resource) => {
                    let resourceUploadRequest = null;
                    if (resource.type === 'file') {
                        resourceUploadRequest = await courseRequests.createCourseFiles(auth.course_id, {
                            ...resource,
                            active: true,
                            created_on: new Date().getTime(),
                            course_id: auth.course_id,
                            type: 'peer-evaluation',
                            peer_evaluation_id: peerEvaluationId,
                            public: true,
                        });
                    } else {
                        resourceUploadRequest = await courseRequests.createCourseSharedUrls(auth.course_id, {
                            ...resource,
                            active: true,
                            created_on: new Date().getTime(),
                            course_id: auth.course_id,
                            type: 'peer-evaluation',
                            peer_evaluation_id: peerEvaluationId,
                            public: true,
                        });
                    }
                    return resourceUploadRequest;
                });
                listOfResources = await Promise.all(uploadFiles);
                data.resources = listOfResources.map((resource) => ({
                    id: resource.id,
                    type: resource.data ? 'file' : 'url',
                }));
            }
            data.rootUrl = window.location.origin;
            const new_data = {
                active: true,
                id: peerEvaluationId,
                course_id: auth.course_id,
                title: data.title,
                format: getFormat(data),
                status: data.status,
                visibility: data.visibility,
                start_on: data.start_on.valueOf(),
                end_on: data.end_on.valueOf(),
                due_on: data.due_on.valueOf(),
                publish_on: data.publish_on,
                release_to_students: data.release_to_students,
                release_on: data.release_on?.valueOf() || 0,
                release_time: data.release_on?.valueOf() || 0,
                keep_result_anonymous: data.keep_result_anonymous,
                is_comment_required: data.is_comment_required,
                min_feedback_length: data.min_feedback_length,
                details: data.details,
                resources: data.resources,
                comment: data.comment, // tooltip text
                rootUrl: window.location.origin,
            };
            if (data.format === 'rubric') {
                new_data.rubric_data = data.rubric_data;
            }
            if (isLTI && data.lti_assignment) {
                const ltiAssignment = LTIAssignments.find(
                    (assignment) => assignment.resourceLinkId === data.lti_assignment
                );
                const ltiAssignmentData = {
                    title: ltiAssignment.label,
                    value: {
                        key: ltiAssignment.resourceLinkId,
                        url: ltiAssignment.id,
                        resourceLinkId: ltiAssignment.resourceLinkId,
                        scoreMaximum: ltiAssignment.scoreMaximum,
                    },
                };
                new_data.lti_assignment = ltiAssignmentData;
            }
            const createPeerEvaluationRequest = await courseRequests.createCoursePeerEvaluation(
                auth.course_id,
                new_data
            );
            setPeerEvaluationList((prev) => [...prev, createPeerEvaluationRequest]);
            setButtonLoading(false);
            return message.success('New peer evaluation was created.');
        } catch (e) {
            console.error(e);
            setButtonLoading(false);
            return message.error('Something went wrong. Please try again later.');
        }
    };

    const updatePeerEvaluation = async (data) => {
        try {
            setButtonLoading(true);
            handleInstructorPages(INSTRUCTOR_PAGES.TABLE);
            if (data?.resources.length > 0) {
                const findExistingFiles = data?.resources.filter((resource) => resource.created_on);
                const uploadFiles = data?.resources.map(async (resource) => {
                    let resourceUploadRequest = null;
                    if (resource.type === 'file' && !resource.created_on) {
                        resourceUploadRequest = await courseRequests.createCourseFiles(auth.course_id, {
                            ...resource,
                            active: true,
                            created_on: new Date().getTime(),
                            course_id: auth.course_id,
                            type: 'peer-evaluation',
                            public: true,
                        });
                    } else if (resource.type === 'link' && !resource.created_on) {
                        resourceUploadRequest = await courseRequests.createCourseSharedUrls(auth.course_id, {
                            ...resource,
                            active: true,
                            created_on: new Date().getTime(),
                            course_id: auth.course_id,
                            type: 'peer-evaluation',
                            public: true,
                        });
                    }
                    return resourceUploadRequest;
                });
                const listOfResources = await Promise.all(uploadFiles);
                data.resources = [...findExistingFiles, ...listOfResources]
                    .filter((item) => item)
                    .map((resource) => ({
                        id: resource.id,
                        type: resource.data ? 'file' : 'url',
                    }));
            }
            const new_data = {
                active: true,
                id: data.id,
                course_id: auth.course_id,
                title: data.title,
                status: data.status,
                format: getFormat(data),
                visibility: data.visibility,
                start_on: data.start_on.valueOf(),
                end_on: data.end_on.valueOf(),
                due_on: data.due_on.valueOf(),
                publish_on: data.publish_on,
                release_to_students: data.release_to_students,
                release_on: data.release_on?.valueOf() || 0,
                release_time: data.release_on?.valueOf() || 0,
                keep_result_anonymous: data.keep_result_anonymous,
                is_comment_required: data.is_comment_required,
                min_feedback_length: data.min_feedback_length,
                details: data.details,
                resources: data.resources,
                comment: data.comment, // tooltip text
            };
            if (data.format === 'rubric') {
                new_data.rubric_data = data.rubric_data;
            }
            if (isLTI && data.lti_assignment) {
                const ltiAssignment = LTIAssignments.find(
                    (assignment) => assignment.resourceLinkId === data.lti_assignment
                );
                const ltiAssignmentData = {
                    title: ltiAssignment.label,
                    value: {
                        key: ltiAssignment.resourceLinkId,
                        url: ltiAssignment.id,
                        resourceLinkId: ltiAssignment.resourceLinkId,
                        scoreMaximum: ltiAssignment.scoreMaximum,
                    },
                };
                new_data.lti_assignment = ltiAssignmentData;
            }
            await courseRequests.updateCoursePeerEvaluation(auth.course_id, new_data);
            await handleListPeerEvaluations();

            setButtonLoading(false);
            return message.success('Your peer evaluation is successfully updated.');
        } catch (e) {
            console.error(e);
            setButtonLoading(false);
            return message.error('Something went wrong.');
        }
    };

    const handleInstructorPages = (page, peerEvaluationId) => {
        switch (page) {
            case INSTRUCTOR_PAGES.TABLE: {
                setCurrentPeerEvaluationId(null);
                setCurrentPeerEvaluation(null);
                setIsViewingSubmission(false);
                setRubricData(initialRubricData);
                return setInstructorPage(page);
            }
            case INSTRUCTOR_PAGES.SUBMISSIONS: {
                const findPeerEvaluation = peerEvaluationList.find(({ id }) => id === peerEvaluationId);
                if (!findPeerEvaluation) return;
                setCurrentPeerEvaluationId(peerEvaluationId);
                setCurrentPeerEvaluation(findPeerEvaluation);
                setIsViewingSubmission(true);
                return setInstructorPage(page);
            }
            case INSTRUCTOR_PAGES.CREATE: {
                const newForm = { ...initialPeerEvaluationState, id: uuid.v4() };
                setCurrentPeerEvaluationId(newForm.id);
                setCurrentPeerEvaluation(newForm);
                setRubricData(initialRubricData);
                return setInstructorPage(page);
            }
            case INSTRUCTOR_PAGES.EDIT: {
                const findPeerEvaluation = peerEvaluationList.find(({ id }) => id === peerEvaluationId);
                if (!findPeerEvaluation) return;
                setCurrentPeerEvaluationId(peerEvaluationId);
                setCurrentPeerEvaluation(findPeerEvaluation);
                return setInstructorPage(page);
            }
            default: {
                return;
            }
        }
    };

    const convertDoubleQuotesToSingle = (text) => {
        return text.toString().replaceAll('"', `""`);
    };

    const exportZeroSumCSV = (findPeerEvaluation) => {
        if (!findPeerEvaluation) return message.error('Please try again later.');
        const peerEvaluationId = findPeerEvaluation.id;
        let maxNumber = 0;
        groups.forEach((group) => {
            const findGroupMembers = studentAccountUsers.filter(
                (member) => member.group_id === group.id && group.number && member.active
            );

            if (findGroupMembers.length > maxNumber) maxNumber = findGroupMembers.length;
        });
        ZEROSUM_CSV_HEADER.push('Team Name');
        ZEROSUM_CSV_HEADER.push('Team Number');
        ZEROSUM_CSV_HEADER.push('Evaluatee');
        ZEROSUM_CSV_HEADER.push('Evaluatee (S#)');
        ZEROSUM_CSV_HEADER.push('Submission Date');
        ZEROSUM_CSV_HEADER.push('Self Evaluation');
        ZEROSUM_CSV_HEADER.push('Self Eval: Comments');

        for (let i = 2; i <= maxNumber; i++) {
            ZEROSUM_CSV_HEADER.push('Evaluator ' + i);
            ZEROSUM_CSV_HEADER.push('Evaluator ' + i + ' (S#)');
            ZEROSUM_CSV_HEADER.push('Evaluation');
            ZEROSUM_CSV_HEADER.push('Evaluator ' + i + ': Comments');
        }
        ZEROSUM_CSV_HEADER.push('Notes');
        ZEROSUM_CSV_HEADER.push('Avg');
        ZEROSUM_CSV_HEADER.push('Raw #');

        const courseDataBlob = (submissions || [])
            .filter((submission) => submission.peer_evaluation_id === peerEvaluationId)
            .map((submission) => {
                const findGroup = groups.find((group) => submission.group_id == group.id);
                if (!findGroup) return;
                const findOwner = studentAccountUsers.find(
                    (member) => member.active && member.owner_account_id === submission.owner_account_id
                );
                const findMembers = studentAccountUsers.filter((member) => member?.group_id === findOwner?.group_id);
                let avg = 0;
                let counter = 1;
                let selfRate = 0;
                selfRate = submission?.data?.[findOwner?.owner_account_id]?.rate;
                avg = avg + parseInt(selfRate) || 0;
                const findOtherSubmissions = submissions
                    .filter(
                        (otherSubmission) =>
                            otherSubmission.peer_evaluation_id === peerEvaluationId &&
                            otherSubmission.group_id === findOwner?.group_id &&
                            otherSubmission.owner_account_id !== findOwner?.owner_account_id
                    )
                    .map((otherSubmission) => {
                        let rate = otherSubmission.data[findOwner?.owner_account_id]?.rate;
                        return {
                            id: otherSubmission,
                            owner_account_id: otherSubmission.owner_account_id,
                            rate,
                            details: otherSubmission.data[findOwner?.owner_account_id]?.details,
                        };
                    });

                const csvRow = [
                    `"${findGroup?.name}"`,
                    `"${findGroup?.number}"`,
                    `"${getUserPreferredFullName(findOwner)}"`,
                    `"${findOwner?.email || findOwner?.lis_person_sourcedid || findOwner?.id}"`,
                    `"${moment(submission?.created_on).format('MMM D[,] YYYY')}"`,
                    `"${selfRate}"`,
                    `"${stripHTML(submission?.data?.[findOwner?.owner_account_id]?.details) || ''}"`,
                ];
                findOtherSubmissions.forEach((data) => {
                    const findMember = studentAccountUsers
                        .filter((student) => student.group_id)
                        .find((member) => member.owner_account_id === data.owner_account_id);
                    avg = avg + parseInt(data.rate || 0);
                    counter = counter + 1;
                    csvRow.push(`"${getUserPreferredFullName(findMember)}"`);
                    csvRow.push(`"${findMember?.email || findMember?.lis_person_sourcedid || findMember?.id}"`);
                    csvRow.push(`"${data.rate}"`);
                    csvRow.push(`"${convertDoubleQuotesToSingle(stripHTML(data.details) || '')}"`);
                });

                if (findOtherSubmissions.length < ZEROSUM_CSV_HEADER.length - 3) {
                    const selfEvalColNumber = 10;
                    const otherEvalColNum = 4;
                    const missingMember =
                        ZEROSUM_CSV_HEADER.length - selfEvalColNumber - findOtherSubmissions.length * otherEvalColNum;
                    for (let i = 0; i < missingMember; i++) {
                        csvRow.push('n/a');
                    }
                }

                const numOfSubmissions = (findOtherSubmissions?.length + 1) / findMembers?.length || 1; // Adding 1 because of self evaluation
                let rawNumber = parseInt(avg) / numOfSubmissions;
                rawNumber = _.round(rawNumber / 100, 2);
                if (rawNumber > 1) rawNumber = 1;
                csvRow.push('');
                csvRow.push(_.round(avg / (findOtherSubmissions?.length + 1), 2));
                csvRow.push(rawNumber);

                return csvRow.toString();
            });
        ZEROSUM_CSV_HEADER = [];
        return courseDataBlob;
    };

    const exportCSVRubric = (findPeerEvaluation) => {
        if (!findPeerEvaluation) return message.error('Please try again later.');
        const peerEvaluationId = findPeerEvaluation.id;
        let csvRows = [];
        let maxNumber = 0;
        let selectedPeerEvaluationTotalRate = 0;
        groups.forEach((group) => {
            const findGroupMembers = studentAccountUsers.filter((member) => member.group_id === group.id);
            if (findGroupMembers.length > maxNumber) maxNumber = findGroupMembers.length;
        });
        const getTotalNum = findPeerEvaluation?.rubric_data?.forEach((row) => {
            const maxRate = parseInt(row.options[0]?.rate);
            selectedPeerEvaluationTotalRate = selectedPeerEvaluationTotalRate + maxRate;
        });
        RUBRIC_CSV_HEADER.push('Team Name');
        RUBRIC_CSV_HEADER.push('Team Number');
        RUBRIC_CSV_HEADER.push('Evaluatee');
        RUBRIC_CSV_HEADER.push('Evaluatee(email)');
        RUBRIC_CSV_HEADER.push('Evaluator');
        RUBRIC_CSV_HEADER.push('Evaluator(email)');
        RUBRIC_CSV_HEADER.push('Submission Date');
        for (let i = 0; i < findPeerEvaluation?.rubric_data?.length; i++) {
            RUBRIC_CSV_HEADER.push(`C${i + 1}`);
        }

        RUBRIC_CSV_HEADER.push('Total');
        RUBRIC_CSV_HEADER.push('Comments');
        RUBRIC_CSV_HEADER.push('Indiv. Avg');
        RUBRIC_CSV_HEADER.push('Raw #');

        const createRows = studentAccountUsers
            .filter((student) => student.group_id)
            ?.sort((a, b) => getUserPreferredFullName(a).localeCompare(getUserPreferredFullName(b)))
            .map((member) => {
                const filteredMemberSubmission = submissions?.filter(
                    (submission) =>
                        submission.peer_evaluation_id === peerEvaluationId &&
                        studentAccountUsers.find((member) => member.owner_account_id === submission.owner_account_id) &&
                        submission?.data?.[member.owner_account_id]
                );
                if (!filteredMemberSubmission.length) return;
                const findGroup = groups.find((group) => member.group_id === group.id);
                let memberRows = [];
                let memberTotalEvaluatedNum = 0;
                let individualAvg = 0;
                let rawNum = 0;

                filteredMemberSubmission.forEach((submission) => {
                    const findOwner = studentAccountUsers.find(
                        (member) => member.owner_account_id === submission.owner_account_id
                    );
                    let total = 0;
                    const csvRow = [
                        `"${convertDoubleQuotesToSingle(findGroup?.name)}"`,
                        `"${findGroup?.number}"`,
                        `"${convertDoubleQuotesToSingle(getUserPreferredFullName(member))}"`,
                        `"${member?.email || member?.lis_person_sourcedid || member?.id}"`,
                        `"${getUserPreferredFullName(findOwner)}"`,
                        `"${findOwner?.email || findOwner?.lis_person_sourcedid || findOwner?.id}"`,
                        `"${moment(submission?.created_on).format('MMM D[,] YYYY')}"`,
                    ];
                    submission.data[member.owner_account_id].rubric_data.forEach((row) => {
                        const findRate = row.options.find((option) => option.id === row.selected_rate_id);
                        total = total + parseInt(findRate?.rate || 0);
                        csvRow.push(`"${findRate?.rate}"`);
                    });
                    csvRow.push(`"${total || '0'}"`);
                    csvRow.push(
                        `"${convertDoubleQuotesToSingle(
                            stripHTML(submission?.data?.[member.owner_account_id]?.details) || ''
                        )}"`
                    );

                    memberTotalEvaluatedNum = memberTotalEvaluatedNum + total;
                    memberRows = [...memberRows, csvRow];
                });
                individualAvg = memberTotalEvaluatedNum / filteredMemberSubmission.length;
                rawNum = individualAvg / selectedPeerEvaluationTotalRate;
                const addAvgAndRawNum = memberRows.map((row) => {
                    const newRow = [...row];
                    newRow.push(`"${_.round(individualAvg, 2) || '0'}"`);
                    newRow.push(`"${_.round(rawNum, 2) || '0'}"`);
                    return newRow.toString();
                });
                csvRows = [...csvRows, ...addAvgAndRawNum];
            });
        RUBRIC_CSV_HEADER = [];
        return csvRows;
    };

    const exportAsCSV = async (peerEvaluationId) => {
        try {
            const findPeerEvaluation = peerEvaluationList.find(({ id }) => id === peerEvaluationId);
            if (!findPeerEvaluation) return message.error('Something went wrong. Please try again later.');
            const isRubric = findPeerEvaluation.format.value === 'rubric';
            let courseDataBlob = null;
            let header = ZEROSUM_CSV_HEADER;
            if (isRubric) {
                header = RUBRIC_CSV_HEADER;
                courseDataBlob = exportCSVRubric(findPeerEvaluation);
            } else {
                courseDataBlob = exportZeroSumCSV(findPeerEvaluation);
            }

            const headerBlob = header.toString().concat('\n');
            const convertedCourseDataBlob = courseDataBlob
                .filter((row) => row?.length > 0)
                .map((item) => item)
                .join('\n');
            const blob = new Blob([headerBlob + convertedCourseDataBlob], {
                type: 'text/plain;charset=utf-8',
            });
            const downloadUrl = URL.createObjectURL(blob);
            return DownloadFileFromURL(downloadUrl, 'ensightful_peer_evaluations.csv');
        } catch (e) {
            console.error(e);
            return message.error('Something went wrong. Please try again later.');
        }
    };

    const handleStudentBackButton = () => {
        setCurrentPeerEvaluationId(null);
        setCurrentPeerEvaluation(null);
        setSubmissionData({});
        setRubricData(initialRubricData);
        setSelectedPeerEvaluation({});
        setIsEditing(false);
        setStudentStep(STUDENT_STEPS.START);
    };

    const handleListPeerEvaluations = async () => {
        try {
            const peerEvaluationList = await courseRequests.listPeerEvaluations(auth.course_id);
            return setPeerEvaluationList(peerEvaluationList);
        } catch (e) {
            console.log(e);
        }
    };

    const listSubmissions = async (peerEvaluationId) => {
        try {
            let submissionList = [];
            let accumulate = 0;
            let listPeerEvaluationRequest = await courseRequests.listSubmissions(
                auth.course_id,
                JSON.stringify({ peer_evaluation_id: peerEvaluationId }),
                'created_on',
                accumulate,
                49
            );
            submissionList = [...submissionList, ...listPeerEvaluationRequest];
            while (listPeerEvaluationRequest?.length === 49) {
                accumulate = accumulate + 49;
                listPeerEvaluationRequest = await courseRequests.listSubmissions(
                    auth.course_id,
                    JSON.stringify({ peer_evaluation_id: peerEvaluationId }),
                    'created_on',
                    accumulate,
                    49
                );
                submissionList = [...submissionList, ...listPeerEvaluationRequest];
            }
            const result = submissionList.reduce((unique, o) => {
                if (!unique.some((obj) => obj.id === o.id)) {
                    unique.push(o);
                }
                return unique;
            }, []);
            setSubmissions(result);
            return submissionList;
        } catch (e) {
            console.error(e);
            return message.error('Something went wrong. Please try again later.');
        }
    };

    const handleEditPeerEvaluation = (peerEvaluationId) => {
        setCurrentPeerEvaluationId(peerEvaluationId);
        setIsViewingSubmission(false);
    };

    const handleUnpublish = async (peerEvaluationId) => {
        try {
            await courseRequests.updateCoursePeerEvaluation(auth.course_id, { id: peerEvaluationId, status: 'draft' });
            await handleListPeerEvaluations();
            handleInstructorPages(INSTRUCTOR_PAGES.TABLE);
            return message.success('Successfully unpublished the peer evaluation.');
        } catch (e) {
            console.log(e);
        }
    };

    const handleReleaseGrades = async (peerEvaluationId) => {
        try {
            await courseRequests.updateCoursePeerEvaluation(auth.course_id, {
                id: peerEvaluationId,
                release_to_students: true,
                is_released: true,
            });
            return message.success('Grades are released.');
        } catch (e) {
            console.log(e);
        }
    };

    const handleDeletePeerEvaluation = async (peerEvaluationId) => {
        try {
            handleInstructorPages(INSTRUCTOR_PAGES.TABLE);
            setPeerEvaluationList((peerEvaluationList) =>
                peerEvaluationList.filter((peerEvaluation) => peerEvaluation.id !== peerEvaluationId)
            );
            await courseRequests.deleteCoursePeerEvaluation(auth.course_id, { id: peerEvaluationId });
            return message.success('Successfully deleted the peer evaluation.');
        } catch (e) {
            console.log(e);
        }
    };

    const notifyPeerEvaluationToStudent = async (peerEvaluationId, accounts = []) => {
        try {
            sendOn('default', {
                payload_type: 'send_peer_evaluation_notifications',
                payload: {
                    course_id: auth.course_id,
                    peer_evaluation_id: peerEvaluationId,
                    accounts,
                    rootUrl: window.location.origin,
                    type: 'reminder',
                },
            });
            return message.success('The notification(s) is sent successfully.');
        } catch (e) {
            console.error(e);
            return message.error('Something went wrong. Please try again later.');
        }
    };

    // LTI
    const listLTIAssignments = async () => {
        if (process.env.REACT_APP_AUTH === 'PASSWORD') return;
        try {
            const assignmentsReq = await ltiService.listLTIAssignments({ course_id: auth.course_id });
            setLTIAssignments(assignmentsReq);
            return assignmentsReq;
        } catch (e) {
            console.error(e);
        }
    };

    const pushGradesToLTI = async (data, submissions) => {
        try {
            setPushGradeLoading(true);
            if (!data.lti_assignment?.value.url) {
                setPushGradeLoading(false);
                return message.error('This peer evaluation is not a canvas assignment.');
            }
            const calculateRates = (data, memberNumber, totalPts) => {
                if (!data || data.length === 0) return 0;
                const divider = memberNumber > data.length ? data.length : memberNumber;
                const rate = data.reduce((acc, cur) => {
                    acc = acc + cur;
                    return acc;
                }, 0);
                const avg = parseInt(rate) / divider;
                return avg / totalPts;
            };

            const calculateZerosumGameRate = (data, memberNumber, totalPts) => {
                if (!data || data.length === 0) return 0;
                const divider = data.length / memberNumber;
                const rate = data.reduce((acc, cur) => {
                    acc = acc + cur;
                    return acc;
                }, 0);
                const avg = parseInt(rate) / divider;
                return avg / totalPts;
            };
            const calculateRowNums = studentAccountUsers
                .filter((member) => member.role === 'student' && member.group_id)
                .map((member) => {
                    const findSubmissions = submissions.filter(
                        (submission) =>
                            submission.peer_evaluation_id === data?.id &&
                            submission.data &&
                            submission.data[member.owner_account_id]
                    );

                    const findCurMemberSubmission = submissions.find(
                        (submission) =>
                            submission.peer_evaluation_id === data?.id &&
                            submission.data &&
                            submission.owner_account_id === member.owner_account_id
                    );
                    const findMemberNumber = studentAccountUsers.filter(
                        (otherMember) => otherMember.group_id === member.group_id
                    );
                    let calculatedRate = 0;
                    if (findCurMemberSubmission) {
                        let totalPts = 0;
                        if (currentPeerEvaluation?.format?.value === 'rubric') {
                            const calculateTotalPts = currentPeerEvaluation?.rubric_data?.forEach((row) => {
                                const maxRate = parseInt(row.options[0]?.rate);
                                totalPts = totalPts + maxRate;
                            });
                        }
                        const constructData = findSubmissions.map((submission) => {
                            let findCurrentUserData = 0;
                            if (currentPeerEvaluation?.format?.value === 'rubric') {
                                findCurrentUserData = submission.data
                                    ? submission.data[member.owner_account_id]?.rubric_data?.reduce((acc, cur) => {
                                          const findRate = cur.options?.find(
                                              (option) => option.id === cur.selected_rate_id
                                          )?.rate;
                                          if (findRate) {
                                              acc = acc + parseInt(findRate);
                                          }
                                          return acc;
                                      }, 0)
                                    : 0;
                            } else {
                                totalPts = 100;
                                findCurrentUserData = submission.data
                                    ? submission.data[member.owner_account_id]?.rate
                                    : 0;
                            }
                            return parseInt(findCurrentUserData);
                        });
                        calculatedRate =
                            currentPeerEvaluation?.format?.value === 'rubric'
                                ? calculateRates(constructData, findMemberNumber.length, totalPts)
                                : calculateZerosumGameRate(constructData, findMemberNumber.length, totalPts);
                    }
                    const passingObj = {
                        owner_account_id: member.owner_account_id,
                        canvas_user_id: member.user_id,
                        activity_progress: 'Completed',
                        grading_progress: 'FullyGraded',
                        submission_url: data.lti_assignment.value.url,
                        scoreMaximum: data.lti_assignment.value.scoreMaximum,
                        rate: calculatedRate,
                        // example of file uploads to LTI
                        // content_data: {
                        //     new_submission: true,
                        //     submission_type: 'online_url',
                        //     submission_data: 'https://instructure.com',
                        //     submitted_at: new Date(),
                        // },
                    };
                    return passingObj;
                });
            if (calculateRowNums?.length > 150) {
                function splitToChunks(array, parts) {
                    let result = [];
                    for (let i = parts; i > 0; i--) {
                        result.push(array.splice(0, Math.ceil(array.length / i)));
                    }
                    return result;
                }
                async function asyncFetch(requests) {
                    return await courseRequests
                        .createLTIAssignmentSubmissions({
                            course_id: auth.course_id,
                            submissions: requests,
                            lti_key: course.lti_key,
                            canvas_domain: course.canvas_domain,
                            memberShipUrl: course.membership_url,
                        })
                        .then((text) => `got back ${text}`);
                }
                async function asyncTimeout(delay) {
                    return await new Promise((resolve) => {
                        setTimeout(() => resolve(delay), delay);
                    }).then((d) => `Waited ${d} seconds`);
                }
                async function runTask(spec) {
                    return spec.task === 'wait' ? await asyncTimeout(spec.duration) : await asyncFetch(spec.requests);
                }
                let durationMultiplier = 0;
                let requestCounter = -1;
                const throttlingRequests = splitToChunks(calculateRowNums, _.round(calculateRowNums.length / 15));
                let asyncThingsToDo = [...Array(throttlingRequests.length * 2)].map((item, index) => {
                    if (index % 2 === 1) {
                        requestCounter = requestCounter + 1;
                        return { task: 'fetch', requests: throttlingRequests[requestCounter] };
                    }
                    durationMultiplier = durationMultiplier + 1;
                    return { task: 'wait', duration: 100 };
                });

                const starterPromise = Promise.resolve(null);
                const log = (result) => console.log(result);
                await asyncThingsToDo.reduce((p, spec) => p.then(() => runTask(spec).then(log)), starterPromise);
            } else {
                await courseRequests.createLTIAssignmentSubmissions({
                    course_id: auth.course_id,
                    submissions: calculateRowNums,
                    lti_key: course.lti_key,
                    canvas_domain: course.canvas_domain,
                    memberShipUrl: course.membership_url,
                });
            }

            setPushGradeLoading(false);
            return message.success('Grades were successfully submitted.');
        } catch (e) {
            console.error(e);
            setPushGradeLoading(false);
            return message.error('Something went wrong. Please try again later.');
        }
    };

    //Student Side
    const startPeerEvaluation = (peerEvaluationId) => {
        setCurrentPeerEvaluationId(peerEvaluationId);
        groupRequest.createGroupActivities(auth.group_id, {
            data: { group_id: auth.group_id, course_id: auth.course_id, peer_evaluation_id: peerEvaluationId },
            type: 'peer_evaluation',
            action: 'click',
        });
    };

    const updateSubmission = async (data) => {
        try {
            const updatedSubmission = await courseRequests.updateCourseSubmission(auth.course_id, data);
            const updatePeerEvaluationList = peerEvaluationList.map((peerEvaluation) => {
                if (peerEvaluation.id === data.peer_evaluation_id) {
                    peerEvaluation.student_submission = updatedSubmission;
                }
                return peerEvaluation;
            });
            setPeerEvaluationList(updatePeerEvaluationList);
            handleStudentBackButton();
            return message.success('Your evaluation has been submitted.');
        } catch (e) {
            console.error(e);
            return message.error('Something went wrong. Please try again later.');
        }
    };

    const createSubmission = async (data) => {
        try {
            setSubmissionLoading(true);
            handleStudentBackButton();
            const createSubmissionReq = await courseRequests.createCourseSubmission(auth.course_id, data);
            await groupRequest.createGroupActivities(auth.group_id, {
                data,
                type: 'peer_evaluation',
                action: 'submit',
            });
            setSubmissions((prev) => [...prev, createSubmissionReq]);
            await handleListPeerEvaluations();
            setSubmissionLoading(false);
            return message.success('Your evaluation has been submitted.');
        } catch (e) {
            console.error(e);
            return message.error('Something went wrong. Please try again later.');
        }
    };

    const handleResetDialog = () => {
        setDialog({
            open: false,
            message: '',
            description: '',
            variant: 'primary',
            actionButtonText: '',
            onAction: () => {},
        });
    };

    const handleOpenDialog = (key, data) => {
        let dialogDetails = {
            open: false,
            message: '',
            description: '',
            variant: 'primary',
            actionButtonText: '',
            onAction: () => {},
        };
        switch (key) {
            case DIALOG.PEER_EVALUATION_PUBLISH: {
                dialogDetails = {
                    open: true,
                    message: 'Publish Evaluation?',
                    description: 'Publishing an evaluation will make it available to students.',
                    variant: 'primary',
                    actionButtonText: 'Publish',
                    onAction: () => {
                        createPeerEvaluation(data);
                        return handleResetDialog();
                    },
                };
                break;
            }
            case DIALOG.PEER_EVALUATION_DELETE: {
                dialogDetails = {
                    open: true,
                    message: 'Delete Evaluation?',
                    description: 'This will permanently delete this peer evaluation.',
                    variant: 'destructive',
                    actionButtonText: 'Delete',
                    onAction: () => {
                        handleStudentBackButton();
                        handleDeletePeerEvaluation(data.id);
                        return handleResetDialog();
                    },
                };
                break;
            }
            case DIALOG.PEER_EVALUATION_UNPUBLISH: {
                dialogDetails = {
                    open: true,
                    message: 'Unpublish Evaluation?',
                    description:
                        'The evaluation will no longer be visible to other users, but existing submissions will saved.',
                    variant: 'destructive',
                    actionButtonText: 'Unpublish',
                    onAction: () => {
                        handleUnpublish(data.id);
                        return handleResetDialog();
                    },
                };
                break;
            }
            case DIALOG.PEER_EVALUATION_SAVE: {
                dialogDetails = {
                    open: true,
                    message: 'Save Evaluation?',
                    description: 'This will permanently delete this peer evaluation.',
                    variant: 'destructive',
                    actionButtonText: 'Delete',
                    onAction: () => {},
                };
                break;
            }
            case DIALOG.PEER_EVALUATION_CANCEL: {
                dialogDetails = {
                    open: true,
                    message: 'Changes Are Not Saved',
                    description:
                        'If you leave this page, your changes will be lost. To save your progress please publish, save as a draft, or schedule post.',
                    variant: 'destructive',
                    actionButtonText: 'Exit',
                    onAction: () => {
                        if (isViewingSubmission) {
                            setInstructorPage(INSTRUCTOR_PAGES.SUBMISSIONS);
                        } else {
                            setInstructorPage(INSTRUCTOR_PAGES.TABLE);
                            setCurrentPeerEvaluationId(null);
                        }
                        return handleResetDialog();
                    },
                };
                break;
            }
            case DIALOG.PEER_EVALUATION_RELEASE_GRADES: {
                dialogDetails = {
                    open: true,
                    message: 'Release Results to Students?',
                    description:
                        'This will immediately release results of this evaluation and cannot be undone. Do you want to continue?',
                    variant: 'primary',
                    actionButtonText: 'Continue',
                    onAction: () => {
                        handleReleaseGrades(data.id);
                        return handleResetDialog();
                    },
                };
                break;
            }
            case DIALOG.PEER_EVALUATION_NOTIFICATIONS_TO_ALL: {
                dialogDetails = {
                    open: true,
                    message: 'Remind All Students?',
                    description:
                        'This will remind students who have not completed their evaluation, do you want to continue?',
                    variant: 'primary',
                    actionButtonText: 'Continue',
                    onAction: () => {
                        notifyPeerEvaluationToStudent(data.id, data.accounts);
                        return handleResetDialog();
                    },
                };
                break;
            }
            case DIALOG.PEER_EVALUATION_NOTIFICATIONS_TO_STUDENT: {
                dialogDetails = {
                    open: true,
                    message: 'Remind to the Student?',
                    description:
                        'This will remind the student who have not completed their evaluation, do you want to continue?',
                    variant: 'primary',
                    actionButtonText: 'Continue',
                    onAction: () => {
                        notifyPeerEvaluationToStudent(data.id, data.accounts);
                        return handleResetDialog();
                    },
                };
                break;
            }
            default:
                return;
        }
        return setDialog(dialogDetails);
    };

    useEffect(() => {
        if (location && auth?.role === 'student') {
            groupRequest.createGroupActivities(auth.group_id, {
                data: { group_id: auth.group_id, course_id: auth.course_id },
                type: 'peer_evaluation',
                action: 'visit',
            });
        }
    }, [location]);

    useEffect(() => {
        if (currentPeerEvaluationId) {
            const findCurrentPeerEvaluation = peerEvaluationList.find(
                (peerEvaluation) => peerEvaluation.id === currentPeerEvaluationId
            );
            setCurrentPeerEvaluation(findCurrentPeerEvaluation);
            (async () => {
                await listSubmissions(currentPeerEvaluationId);
            })();
        }
    }, [currentPeerEvaluationId]);

    useEffect(() => {
        if (auth) {
            (async () => {
                await handleListPeerEvaluations();
            })();
        }
    }, [auth]);

    return (
        <PageContext.Provider
            value={{
                auth,
                buttonLoading,
                setButtonLoading,
                dialog,
                setDialog,
                handleOpenDialog,
                currentPeerEvaluationId,
                setCurrentPeerEvaluationId,
                currentPeerEvaluation,
                isViewingSubmission,
                setIsViewingSubmission,
                peerEvaluationList,
                members: studentAccountUsers || [],
                submissions,
                listSubmissions,

                //Instructor
                initialPeerEvaluationState,
                form,
                setForm,
                instructorPage,
                setInstructorPage,
                createPeerEvaluation,
                updatePeerEvaluation,
                handleEditPeerEvaluation,
                handleUnpublish,
                handleReleaseGrades,
                handleDeletePeerEvaluation,
                handleInstructorPages,
                exportAsCSV,

                //LTI
                pushGradeLoading,
                LTIAssignments,
                listLTIAssignments,
                pushGradesToLTI,

                //Student
                submissionLoading,
                studentStep,
                setStudentStep,
                selectedPeerEvaluation,
                setSelectedPeerEvaluation,
                submissionData,
                setSubmissionData,
                isEditing,
                setIsEditing,
                rubricData,
                setRubricData,
                startPeerEvaluation,
                updateSubmission,
                createSubmission,
                handleStudentBackButton,

                //Constants
                DIALOG,
                INSTRUCTOR_PAGES,
                STUDENT_STEPS,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
